import type {Components, Theme} from '@mui/material/styles';
import {
    DATA_GRID_GROUP_CHILD_ROW_CLASS,
    DATA_GRID_GROUP_ROW_EXPANDED_CLASS,
    DATA_GRID_ROW_MIN_HEIGHT,
} from '~/components/data-grid/data-grid.config';
import FilterIcon from '~/components/icons/svg/FilterIcon.svg?react';
import {COLORS} from '~/style/colors-ts';

/** Header filter text field height (fits in the 52px filter row). */
const HEADER_FILTER_INPUT_HEIGHT = DATA_GRID_ROW_MIN_HEIGHT.small;

const minRowHeightStyles = (minHeight: number) => ({
    [`&.data-grid-min-row-${minHeight} .MuiDataGrid-row`]: {
        minHeight: minHeight,
    },
    // getRowHeight={() => 'auto'} sets inline minHeight: 'auto' — override so rows never shrink below min.
    [`&.data-grid-min-row-${minHeight} .MuiDataGrid-row--dynamicHeight`]: {
        minHeight: `${minHeight}px !important`,
    },
    [`&.data-grid-min-row-${minHeight}.auto-row-height-centered .MuiDataGrid-row--dynamicHeight .MuiDataGrid-cell`]: {
        minHeight: '100%',
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
    },
});

/** Checkbox stays 36px tall regardless of row min height (6px padding + 24px icon). */
const SELECTION_CHECKBOX_PADDING = Math.max(0, (DATA_GRID_ROW_MIN_HEIGHT.small - 24) / 2);

const selectionCheckboxStyles = {
    [`& .MuiDataGrid-cellCheckbox .MuiCheckbox-root`]: {
        padding: SELECTION_CHECKBOX_PADDING,
    },
    [`& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root`]: {
        padding: SELECTION_CHECKBOX_PADDING,
    },
};

const pinnedCellBackground = (backgroundColor: string) => ({
    [`& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight`]: {
        backgroundColor,
    },
});

const groupingRowBackground = (backgroundColor: string) => ({
    backgroundColor,
    [`& .MuiDataGrid-cell`]: {
        backgroundColor,
    },
    ...pinnedCellBackground(backgroundColor),
});

/** Matches small row height; MUI TablePagination toolbar defaults to 52px. */
const PAGINATION_TOOLBAR_MIN_HEIGHT = DATA_GRID_ROW_MIN_HEIGHT.small;

const paginationStyles = {
    [`& .MuiTablePagination-root`]: {
        minHeight: PAGINATION_TOOLBAR_MIN_HEIGHT + 2,
        height: PAGINATION_TOOLBAR_MIN_HEIGHT + 2,
    },
    [`& .MuiTablePagination-toolbar`]: {
        minHeight: PAGINATION_TOOLBAR_MIN_HEIGHT,
        height: PAGINATION_TOOLBAR_MIN_HEIGHT,
    },
};

const groupingRowStyles = {
    [`& .MuiDataGrid-row.${DATA_GRID_GROUP_ROW_EXPANDED_CLASS}`]: {
        ...groupingRowBackground(COLORS.mithraBlue[100]),
        '&:hover': groupingRowBackground(COLORS.mithraBlue[100]),
    },
    [`& .MuiDataGrid-row.${DATA_GRID_GROUP_CHILD_ROW_CLASS}`]: {
        ...groupingRowBackground(COLORS.grey[50]),
        '&:hover': groupingRowBackground(COLORS.grey[150]),
    },
};

export const dataGridOverrides: Components<Theme>['MuiDataGrid'] = {
    defaultProps: {
        slots: {
            openFilterButtonIcon: FilterIcon,
        },
    },
    styleOverrides: {
        root: ({theme}) => ({
            '--DataGrid-t-color-border-base': theme.palette.divider,
            '--DataGrid-rowBorderColor': theme.palette.divider,
            borderColor: theme.palette.divider,
            ...minRowHeightStyles(DATA_GRID_ROW_MIN_HEIGHT.small),
            ...minRowHeightStyles(DATA_GRID_ROW_MIN_HEIGHT.medium),
            ...selectionCheckboxStyles,
            ...paginationStyles,
            ...groupingRowStyles,
            [`& .MuiDataGrid-row.hidden-row, & .MuiDataGrid-row[data-id^="auto-generated-row"]`]: {
                display: 'none',
            },
        }),
        cell: {
            [`& > :first-child`]: {
                height: '100%',
            },
            '&.hidden-cell': {
                display: 'none',
            },
            '& .hidden-cell': {
                display: 'none',
            },
        },
        columnSeparator: ({theme}) => ({
            color: theme.palette.divider,
        }),
        toolbar: {
            minHeight: 44,
        },
        // Overrides compact density default (input height: 20).
        columnHeaderFilterInput: {
            marginBottom: 0,
            [`& .MuiInputBase-root`]: {
                height: HEADER_FILTER_INPUT_HEIGHT,
            },
            [`& .MuiInputBase-input`]: {
                height: HEADER_FILTER_INPUT_HEIGHT,
                boxSizing: 'border-box',
                paddingTop: 0,
                paddingBottom: 0,
            },
        },
    },
};
